import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  ArrowLeft, Bell, BellRinging, MapPin, Calendar, CurrencyEur, MusicNotes, Buildings,
  Trash, Plus, MagnifyingGlass, X, Trophy, Confetti, PencilSimple, Globe,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format, parseISO, differenceInDays, isBefore } from 'date-fns';
import { it } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Badge({ type }) {
  const CONF = {
    concerto: { style: 'bg-rose-50 text-rose-700 border-rose-200', icon: <MusicNotes size={11} weight="bold" />, label: 'concerto' },
    fiera: { style: 'bg-blue-50 text-blue-700 border-blue-200', icon: <Buildings size={11} weight="bold" />, label: 'fiera' },
    sportivo: { style: 'bg-amber-50 text-amber-700 border-amber-200', icon: <Trophy size={11} weight="bold" />, label: 'sport' },
    festival: { style: 'bg-violet-50 text-violet-700 border-violet-200', icon: <Confetti size={11} weight="bold" />, label: 'festival' },
  };
  const cfg = CONF[type] || CONF.fiera;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${cfg.style}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function EventCard({ event, isSubscribed, onSubscribe, currentUserId, onEdit, onDelete }) {
  const today = new Date();
  const start = parseISO(event.start_date);
  const end = parseISO(event.end_date || event.start_date);
  const isPast = isBefore(end, today);
  const daysTo = differenceInDays(start, today);
  const multiDay = event.start_date !== event.end_date;

  let relLabel = '';
  if (isPast) relLabel = 'Passato';
  else if (daysTo === 0) relLabel = 'Oggi';
  else if (daysTo === 1) relLabel = 'Domani';
  else if (daysTo < 30) relLabel = `Tra ${daysTo}g`;
  else if (daysTo < 60) relLabel = `Tra ${Math.floor(daysTo / 7)}sett`;
  else relLabel = `Tra ${Math.floor(daysTo / 30)} mesi`;

  const relColor = isPast ? 'text-zinc-300' : daysTo < 31 ? 'text-amber-600' : 'text-emerald-600';
  const isMine = event.source === 'user' && event.created_by && currentUserId && event.created_by === currentUserId;

  return (
    <article
      className={`group bg-white border ${isMine ? 'border-emerald-300' : 'border-zinc-200'} hover:border-zinc-900 transition-all p-5 sm:p-6 ${isPast ? 'opacity-40' : ''}`}
      data-testid={`event-card-${event.name.replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge type={event.type} />
          {isMine && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`event-mine-badge-${event.id}`}>
              <Globe size={10} weight="bold" /> Tuo evento
            </span>
          )}
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-wider ${relColor}`}>{relLabel}</span>
      </div>
      <h3 className="text-base sm:text-lg font-black text-zinc-900 leading-tight mb-2">{event.name}</h3>
      <div className="space-y-1.5 text-xs sm:text-sm">
        <div className="flex items-center gap-1.5 text-zinc-700">
          <Calendar size={14} weight="bold" className="flex-shrink-0 text-zinc-400" />
          <span className="font-medium">
            {multiDay
              ? `${format(start, 'd MMM', { locale: it })} → ${format(end, 'd MMM yyyy', { locale: it })}`
              : format(start, 'EEE d MMMM yyyy', { locale: it })}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-zinc-700">
          <MapPin size={14} weight="bold" className="flex-shrink-0 text-zinc-400" />
          <span className="font-medium">{event.city}</span>
          {event.provincia && <span className="text-zinc-400 text-[11px]">({event.provincia})</span>}
          {event.region && <span className="text-zinc-400 text-[11px]">· {event.region}</span>}
        </div>
        {event.venue && (
          <div className="flex items-center gap-1.5 text-zinc-500 text-[11px] sm:text-xs italic">
            <span className="ml-4">{event.venue}</span>
          </div>
        )}
        {event.url && (
          <div className="flex items-center gap-1.5 text-[11px]">
            <a
              href={event.url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-4 text-emerald-700 hover:text-emerald-900 underline break-all"
            >
              Sito ufficiale →
            </a>
          </div>
        )}
      </div>
      {isMine && (
        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={() => onEdit(event)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-[11px] font-bold uppercase tracking-wider border border-zinc-300 text-zinc-700 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors"
            data-testid={`edit-event-btn-${event.id}`}
          >
            <PencilSimple size={12} weight="bold" /> Modifica
          </button>
          <button
            onClick={() => onDelete(event)}
            className="inline-flex items-center justify-center gap-1 py-2 px-3 text-[11px] font-bold uppercase tracking-wider border border-red-200 text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
            data-testid={`delete-event-btn-${event.id}`}
            aria-label="Elimina evento"
          >
            <Trash size={12} weight="bold" />
          </button>
        </div>
      )}
      {!isPast && !isMine && (
        <button
          onClick={() => onSubscribe(event.city)}
          className={`mt-4 w-full flex items-center justify-center gap-2 py-2 text-[11px] font-bold uppercase tracking-wider border transition-colors ${
            isSubscribed
              ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
              : 'border-zinc-300 text-zinc-700 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white'
          }`}
          data-testid={`subscribe-btn-${event.city}`}
        >
          {isSubscribed ? (
            <><BellRinging size={13} weight="bold" /> Alert attivo per {event.city}</>
          ) : (
            <><Bell size={13} weight="bold" /> Attiva alert per {event.city}</>
          )}
        </button>
      )}
    </article>
  );
}

export default function EventiPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCity, setFilterCity] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [search, setSearch] = useState('');
  const [showAlertsDialog, setShowAlertsDialog] = useState(false);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  const [welcomeCity, setWelcomeCity] = useState('');
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    name: '', type: 'fiera', city: '', region: '', provincia: '',
    venue: '', start_date: '', end_date: '', url: '', description: '',
  });
  const [eventSaving, setEventSaving] = useState(false);

  const resetEventForm = () => {
    setEditingEvent(null);
    setEventForm({
      name: '', type: 'fiera', city: '', region: '', provincia: '',
      venue: '', start_date: '', end_date: '', url: '', description: '',
    });
  };

  const openNewEventDialog = () => {
    resetEventForm();
    setShowEventDialog(true);
  };

  const openEditEventDialog = (ev) => {
    setEditingEvent(ev);
    setEventForm({
      name: ev.name || '', type: ev.type || 'fiera', city: ev.city || '',
      region: ev.region || '', provincia: ev.provincia || '',
      venue: ev.venue || '', start_date: ev.start_date || '',
      end_date: ev.end_date || '', url: ev.url || '', description: ev.description || '',
    });
    setShowEventDialog(true);
  };

  const handleSaveEvent = async () => {
    if (!eventForm.name.trim() || !eventForm.city.trim() || !eventForm.start_date) {
      toast.error('Nome, città e data di inizio sono obbligatori');
      return;
    }
    setEventSaving(true);
    try {
      const payload = { ...eventForm };
      if (!payload.end_date) delete payload.end_date;
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, payload, { withCredentials: true });
        toast.success('Evento aggiornato — RSS feed sincronizzato');
      } else {
        await axios.post(`${API}/events`, payload, { withCredentials: true });
        toast.success('Evento pubblicato! Visibile nel feed RSS pubblico');
      }
      setShowEventDialog(false);
      resetEventForm();
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio evento');
    } finally {
      setEventSaving(false);
    }
  };

  const handleDeleteEvent = async (ev) => {
    if (!window.confirm(`Eliminare l'evento "${ev.name}" (${ev.city})?\n\nSarà rimosso anche dal feed RSS pubblico.`)) return;
    try {
      await axios.delete(`${API}/events/${ev.id}`, { withCredentials: true });
      toast.success('Evento eliminato');
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore eliminazione');
    }
  };
  const welcomePromptShownRef = useRef(false);

  const loadData = async () => {
    try {
      const [evRes, alRes] = await Promise.all([
        axios.get(`${API}/events?limit=2000`, { withCredentials: true }),
        axios.get(`${API}/events/alerts`, { withCredentials: true }),
      ]);
      setEvents(evRes.data.events || []);
      setAlerts(alRes.data.alerts || []);
      // Welcome prompt: se nessun alert attivo e non già mostrato in sessione
      if (!welcomePromptShownRef.current && (alRes.data.alerts || []).length === 0) {
        const dismissed = sessionStorage.getItem('eventi_welcome_dismissed');
        if (!dismissed) {
          welcomePromptShownRef.current = true;
          setTimeout(() => setShowWelcomeDialog(true), 600);
        }
      }
    } catch (e) {
      toast.error('Errore caricamento eventi');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribedCities = useMemo(() => new Set(alerts.map(a => a.city.toLowerCase())), [alerts]);

  const allCities = useMemo(() => {
    return Array.from(new Set(events.map(e => e.city).filter(Boolean))).sort();
  }, [events]);

  const filtered = useMemo(() => {
    const now = new Date();
    return events
      .filter(e => {
        if (filterCity !== 'all' && e.city !== filterCity) return false;
        if (filterType !== 'all' && e.type !== filterType) return false;
        if (search && !e.name.toLowerCase().includes(search.toLowerCase()) &&
            !e.city.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
      })
      .sort((a, b) => {
        const aPast = isBefore(parseISO(a.end_date || a.start_date), now);
        const bPast = isBefore(parseISO(b.end_date || b.start_date), now);
        if (aPast !== bPast) return aPast ? 1 : -1;
        return a.start_date.localeCompare(b.start_date);
      });
  }, [events, filterCity, filterType, search]);

  const upcomingCount = useMemo(() => {
    const now = new Date();
    return events.filter(e => !isBefore(parseISO(e.end_date || e.start_date), now)).length;
  }, [events]);

  const handleSubscribe = async (city) => {
    const already = subscribedCities.has(city.toLowerCase());
    try {
      if (already) {
        const alert = alerts.find(a => a.city.toLowerCase() === city.toLowerCase());
        if (alert) {
          await axios.delete(`${API}/events/alerts/${alert.id}`, { withCredentials: true });
          toast.success(`Alert disattivato per ${city}`);
        }
      } else {
        await axios.post(`${API}/events/alerts`, { city, types: ['concerto', 'fiera', 'sportivo', 'festival'] }, { withCredentials: true });
        toast.success(`Alert attivato per ${city}! Ti avviseremo 60 giorni prima di ogni evento.`);
      }
      await loadData();
    } catch (e) {
      toast.error('Operazione fallita');
    }
  };

  const handleDeleteAlert = async (id) => {
    try {
      await axios.delete(`${API}/events/alerts/${id}`, { withCredentials: true });
      toast.success('Alert rimosso');
      await loadData();
    } catch (e) {
      toast.error('Errore');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="eventi-page">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-600 hover:text-zinc-900 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={14} weight="bold" /> Dashboard
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={openNewEventDialog}
              className="hidden sm:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider bg-emerald-600 text-white px-3 sm:px-4 py-2 hover:bg-emerald-700 transition-colors"
              data-testid="add-event-btn"
            >
              <Plus size={14} weight="bold" /> Aggiungi evento
            </button>
            <button
              onClick={openNewEventDialog}
              className="sm:hidden inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-emerald-600 text-white px-2.5 py-2 hover:bg-emerald-700 transition-colors"
              data-testid="add-event-btn-mobile"
              aria-label="Aggiungi evento"
            >
              <Plus size={14} weight="bold" />
            </button>
            <button
              onClick={() => setShowAlertsDialog(true)}
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider bg-zinc-900 text-white px-3 sm:px-4 py-2 hover:bg-zinc-800 transition-colors"
              data-testid="my-alerts-btn"
            >
              <BellRinging size={14} weight="bold" /> I miei alert
              {alerts.length > 0 && (
                <span className="bg-emerald-500 text-white text-[10px] px-1.5 rounded-full">{alerts.length}</span>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero */}
        <section className="mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-3 py-1 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-4">
            <CurrencyEur size={13} weight="bold" /> Pricing intelligence
          </div>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-[1.05] text-zinc-900 mb-3" data-testid="eventi-h1">
            Hai sistemato i tuoi prezzi?
          </h1>
          <p className="text-sm sm:text-base text-zinc-600 max-w-2xl leading-relaxed">
            Tutte le <strong>fiere</strong> e i <strong>concerti</strong> programmati in Italia nel 2026.
            Attiva l'alert sulla tua città e ti mandiamo un'email <strong>60 giorni prima</strong> di ogni evento,
            così puoi rivedere i prezzi al rialzo con calma.
          </p>
          <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-6 sm:mt-8 max-w-xl">
            <div className="bg-white border border-zinc-200 p-3 sm:p-4">
              <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">In programma</p>
              <p className="text-2xl sm:text-3xl font-black text-zinc-900">{upcomingCount}</p>
            </div>
            <div className="bg-white border border-zinc-200 p-3 sm:p-4">
              <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Città</p>
              <p className="text-2xl sm:text-3xl font-black text-zinc-900">{allCities.length}</p>
            </div>
            <div className="bg-white border border-zinc-200 p-3 sm:p-4">
              <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Tuoi alert</p>
              <p className="text-2xl sm:text-3xl font-black text-emerald-600">{alerts.length}</p>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="bg-white border border-zinc-200 p-4 sm:p-5 mb-5 flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Cerca</label>
            <div className="relative">
              <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                type="text"
                placeholder="Nome evento o città"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 rounded-none border-zinc-300"
                data-testid="event-search"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:flex gap-3">
            <div className="min-w-[140px]">
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Tipo</label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="rounded-none border-zinc-300" data-testid="filter-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="concerto">🎤 Concerti</SelectItem>
                  <SelectItem value="fiera">🏢 Fiere</SelectItem>
                  <SelectItem value="sportivo">🏆 Sportivi</SelectItem>
                  <SelectItem value="festival">🎉 Festival</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px]">
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Città</label>
              <Select value={filterCity} onValueChange={setFilterCity}>
                <SelectTrigger className="rounded-none border-zinc-300" data-testid="filter-city">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="all">Tutte</SelectItem>
                  {allCities.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {(search || filterCity !== 'all' || filterType !== 'all') && (
            <button
              onClick={() => { setSearch(''); setFilterCity('all'); setFilterType('all'); }}
              className="self-end text-[11px] text-zinc-500 hover:text-zinc-900 underline whitespace-nowrap"
            >
              Resetta filtri
            </button>
          )}
        </section>

        {/* Results */}
        {loading ? (
          <p className="text-center text-sm text-zinc-500 py-12">Caricamento eventi...</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white border border-zinc-200" data-testid="no-events">
            <p className="text-sm text-zinc-500">Nessun evento corrisponde ai filtri selezionati.</p>
          </div>
        ) : (
          <>
            <p className="text-xs text-zinc-500 mb-4 font-medium">
              {filtered.length} evento/i trovato/i
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {filtered.map((ev, i) => (
                <EventCard
                  key={ev.id || `${ev.name}-${ev.start_date}-${ev.city}-${i}`}
                  event={ev}
                  isSubscribed={subscribedCities.has(ev.city.toLowerCase())}
                  onSubscribe={handleSubscribe}
                  currentUserId={user?.id}
                  onEdit={openEditEventDialog}
                  onDelete={handleDeleteEvent}
                />
              ))}
            </div>
          </>
        )}

        {/* Info footer */}
        <div className="mt-12 bg-zinc-900 text-white p-6 sm:p-8">
          <h3 className="text-base sm:text-lg font-black mb-2">Come funzionano gli alert?</h3>
          <ul className="space-y-1.5 text-xs sm:text-sm text-zinc-300 leading-relaxed">
            <li>→ Attivi l'alert su una città (puoi averne più di uno)</li>
            <li>→ 60 giorni prima di ogni concerto/fiera nella città selezionata, ti arriva un'email con nome evento, data e venue</li>
            <li>→ Decidi se alzare i prezzi sulle tue strutture in zona</li>
            <li>→ Fonti aggiornate: <span className="text-emerald-400">AEFI.it</span> (fiere) e <span className="text-emerald-400">Teamworld.it</span> (concerti)</li>
            <li>→ <span className="text-emerald-400">Feed RSS pubblico</span>: <code className="text-[11px] bg-black/40 px-1.5 py-0.5">/api/rss/eventi.xml</code> (?city=... per filtrare) — i tuoi eventi aggiunti qui compaiono automaticamente in Hostland</li>
          </ul>
        </div>
      </main>

      {/* Add / Edit Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={(open) => {
        setShowEventDialog(open);
        if (!open) resetEventForm();
      }}>
        <DialogContent className="max-w-xl rounded-none border-zinc-300 max-h-[90vh] overflow-y-auto" data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-black tracking-tight">
              {editingEvent ? 'Modifica evento' : 'Aggiungi evento personalizzato'}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500 leading-relaxed">
              L'evento verrà pubblicato subito nella lista pubblica e nel <strong>feed RSS</strong> consumato da Hostland (<code className="text-[10px] bg-zinc-100 px-1">/api/rss/eventi.xml</code>).
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Nome evento *</Label>
              <Input
                value={eventForm.name}
                onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                placeholder="Es. Salone del Mobile 2026"
                className="rounded-none border-zinc-300"
                data-testid="event-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Tipo *</Label>
                <Select value={eventForm.type} onValueChange={(v) => setEventForm({ ...eventForm, type: v })}>
                  <SelectTrigger className="rounded-none border-zinc-300" data-testid="event-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fiera">Fiera</SelectItem>
                    <SelectItem value="concerto">Concerto</SelectItem>
                    <SelectItem value="sportivo">Evento sportivo</SelectItem>
                    <SelectItem value="festival">Festival</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Città *</Label>
                <Input
                  value={eventForm.city}
                  onChange={(e) => setEventForm({ ...eventForm, city: e.target.value })}
                  placeholder="Milano"
                  className="rounded-none border-zinc-300"
                  data-testid="event-city-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Regione</Label>
                <Input
                  value={eventForm.region}
                  onChange={(e) => setEventForm({ ...eventForm, region: e.target.value })}
                  placeholder="Lombardia"
                  className="rounded-none border-zinc-300"
                />
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Provincia</Label>
                <Input
                  value={eventForm.provincia}
                  onChange={(e) => setEventForm({ ...eventForm, provincia: e.target.value.toUpperCase() })}
                  placeholder="MI"
                  maxLength={2}
                  className="rounded-none border-zinc-300 uppercase"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Luogo / Venue</Label>
              <Input
                value={eventForm.venue}
                onChange={(e) => setEventForm({ ...eventForm, venue: e.target.value })}
                placeholder="Rho Fiera · Milano"
                className="rounded-none border-zinc-300"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Data inizio *</Label>
                <Input
                  type="date"
                  value={eventForm.start_date}
                  onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                  className="rounded-none border-zinc-300"
                  data-testid="event-start-date"
                />
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Data fine</Label>
                <Input
                  type="date"
                  value={eventForm.end_date}
                  onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                  className="rounded-none border-zinc-300"
                  data-testid="event-end-date"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">URL sito ufficiale</Label>
              <Input
                value={eventForm.url}
                onChange={(e) => setEventForm({ ...eventForm, url: e.target.value })}
                placeholder="https://..."
                className="rounded-none border-zinc-300"
              />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Descrizione</Label>
              <textarea
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                rows={3}
                maxLength={2000}
                placeholder="Breve descrizione pubblica..."
                className="w-full rounded-none border border-zinc-300 p-2 text-sm font-sans"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              onClick={() => { setShowEventDialog(false); resetEventForm(); }}
              variant="outline"
              className="rounded-none border-zinc-300 font-bold"
              data-testid="event-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              onClick={handleSaveEvent}
              disabled={eventSaving}
              className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              data-testid="event-save-btn"
            >
              {eventSaving ? 'Salvataggio...' : editingEvent ? 'Aggiorna' : 'Pubblica evento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* My Alerts Dialog */}
      <Dialog open={showAlertsDialog} onOpenChange={setShowAlertsDialog}>
        <DialogContent className="max-w-md rounded-none border-zinc-300" data-testid="alerts-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-black tracking-tight">I miei alert</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Ti mandiamo un'email 60 giorni prima di ogni evento nelle città selezionate.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-2 max-h-96 overflow-y-auto">
            {alerts.length === 0 ? (
              <p className="text-sm text-zinc-400 text-center py-6">Nessun alert attivo.</p>
            ) : alerts.map(a => (
              <div key={a.id} className="flex items-center justify-between border border-zinc-200 px-4 py-3">
                <div>
                  <p className="font-bold text-sm text-zinc-900 flex items-center gap-2">
                    <MapPin size={13} weight="bold" className="text-emerald-600" /> {a.city}
                  </p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">
                    {(a.types || []).join(' · ')} · attivo
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteAlert(a.id)}
                  className="text-zinc-400 hover:text-red-500 transition-colors"
                  data-testid={`delete-alert-${a.city}`}
                >
                  <Trash size={16} weight="bold" />
                </button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowAlertsDialog(false)} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white">
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Welcome prompt: subscribe to your city now */}
      <Dialog open={showWelcomeDialog} onOpenChange={(open) => {
        setShowWelcomeDialog(open);
        if (!open) sessionStorage.setItem('eventi_welcome_dismissed', '1');
      }}>
        <DialogContent className="max-w-md rounded-none border-zinc-300 overflow-hidden p-0" data-testid="welcome-dialog">
          <div className="bg-gradient-to-br from-rose-500 via-rose-600 to-pink-700 text-white p-6 sm:p-7 relative">
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 30% 30%, white 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-white/15 border border-white/30 backdrop-blur px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Non perdere un colpo
              </div>
              <DialogTitle className="text-2xl font-black tracking-tight leading-tight mb-1">
                Ti avvisiamo prima degli altri
              </DialogTitle>
              <DialogDescription className="text-sm text-white/85 leading-relaxed">
                Scegli la città della tua struttura: ti mandiamo un'email <strong className="text-white">60 giorni prima</strong> di ogni concerto, fiera o evento sportivo in arrivo. Ora non potrai più dire <em>"l'avessi saputo prima..."</em>.
              </DialogDescription>
            </div>
          </div>

          <div className="p-6 sm:p-7 space-y-4 bg-white">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2 block">
                In quale città affitti?
              </Label>
              <Select value={welcomeCity} onValueChange={setWelcomeCity}>
                <SelectTrigger className="rounded-none border-zinc-300 h-11" data-testid="welcome-city-select">
                  <SelectValue placeholder="Seleziona la tua città" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {allCities.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-400 mt-1.5">Potrai aggiungerne altre in qualsiasi momento.</p>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 p-3 text-[11px] text-emerald-800 leading-relaxed">
              ✨ Il primo alert lo ricevi per email entro 24 ore se c'è un evento a meno di 60 giorni.
            </div>

            <div className="flex gap-2 pt-1">
              <Button
                variant="outline"
                onClick={() => {
                  sessionStorage.setItem('eventi_welcome_dismissed', '1');
                  setShowWelcomeDialog(false);
                }}
                className="rounded-none flex-1"
                data-testid="welcome-skip-btn"
              >
                Più tardi
              </Button>
              <Button
                onClick={async () => {
                  if (!welcomeCity) { toast.error('Seleziona una città'); return; }
                  try {
                    await axios.post(`${API}/events/alerts`, {
                      city: welcomeCity,
                      types: ['concerto', 'fiera', 'sportivo', 'festival'],
                    }, { withCredentials: true });
                    toast.success(`Alert attivato per ${welcomeCity}! 🎉`);
                    sessionStorage.setItem('eventi_welcome_dismissed', '1');
                    setShowWelcomeDialog(false);
                    setWelcomeCity('');
                    await loadData();
                  } catch (e) {
                    toast.error('Errore, riprova.');
                  }
                }}
                disabled={!welcomeCity}
                className="rounded-none flex-1 bg-gradient-to-br from-rose-500 to-pink-700 hover:opacity-90 text-white font-bold uppercase text-xs tracking-wider"
                data-testid="welcome-subscribe-btn"
              >
                <Bell size={14} weight="bold" className="mr-1.5" /> Attiva alert
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
